import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import '../styles/ResizableColumns.css';

const MIN_COLUMN_WIDTH = 50;
const SAVE_DELAY_MS = 600;

const getCurrentUserId = () => {
    try {
        return JSON.parse(localStorage.getItem('user') || 'null')?.id || 'anon';
    } catch (_) {
        return 'anon';
    }
};

const cacheKey = key => `ui-setting:${getCurrentUserId()}:${key}`;

const readCache = key => {
    try {
        return JSON.parse(localStorage.getItem(cacheKey(key)) || 'null');
    } catch (_) {
        return null;
    }
};

const writeCache = (key, value) => {
    try {
        localStorage.setItem(cacheKey(key), JSON.stringify(value));
    } catch (_) {
        /* квота localStorage — сервер всё равно сохранит */
    }
};

const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`
});

/**
 * Персональная UI-настройка пользователя: хранится на сервере
 * (/api/user-ui-settings/:key), localStorage — только кеш для мгновенного показа.
 */
export const useUserUiSetting = (key, defaultValue) => {
    const [value, setValueState] = useState(() => readCache(key) ?? defaultValue);
    const saveTimerRef = useRef(null);
    const pendingRef = useRef(false);
    const latestRef = useRef(value);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`/api/user-ui-settings/${encodeURIComponent(key)}`, { headers: authHeaders() })
            .then(res => {
                if (cancelled || res.data?.value == null) return;
                latestRef.current = res.data.value;
                setValueState(res.data.value);
                writeCache(key, res.data.value);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [key]);

    const flush = useCallback(() => {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
        if (!pendingRef.current) return;
        pendingRef.current = false;
        axios
            .put(
                `/api/user-ui-settings/${encodeURIComponent(key)}`,
                { value: latestRef.current },
                { headers: authHeaders() }
            )
            .catch(() => {});
    }, [key]);

    useEffect(() => flush, [flush]);

    const setValue = useCallback(
        updater => {
            const next =
                typeof updater === 'function' ? updater(latestRef.current) : updater;
            latestRef.current = next;
            setValueState(next);
            writeCache(key, next);
            pendingRef.current = true;
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = setTimeout(flush, SAVE_DELAY_MS);
        },
        [key, flush]
    );

    return [value, setValue];
};

const startColumnDrag = (event, { minWidth, onResize }) => {
    event.preventDefault();
    event.stopPropagation();

    const th = event.currentTarget.parentElement;
    const startX = event.clientX;
    const startWidth = th.getBoundingClientRect().width;
    const tableRect = (th.closest('.ant-table') || th).getBoundingClientRect();

    const guide = document.createElement('div');
    guide.className = 'resizable-col-guide';
    guide.style.top = `${Math.max(tableRect.top, 0)}px`;
    guide.style.height = `${Math.min(tableRect.bottom, window.innerHeight) - Math.max(tableRect.top, 0)}px`;
    guide.style.left = `${startX}px`;
    document.body.appendChild(guide);

    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    let nextWidth = startWidth;

    const onMove = moveEvent => {
        nextWidth = Math.max(minWidth, startWidth + moveEvent.clientX - startX);
        guide.style.left = `${startX + nextWidth - startWidth}px`;
    };

    const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        guide.remove();
        document.body.style.cursor = prevCursor;
        document.body.style.userSelect = prevSelect;
        if (Math.abs(nextWidth - startWidth) >= 2) {
            onResize(Math.round(nextWidth));
        }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
};

export const ResizableTitle = ({ onResize, onResetWidth, minWidth, width, ...rest }) => {
    if (!onResize) {
        return <th {...rest} />;
    }

    const style = rest.style?.position ? rest.style : { ...rest.style, position: 'relative' };

    return (
        <th {...rest} style={style}>
            {rest.children}
            <span
                className="resizable-col-handle"
                title="Потяните, чтобы изменить ширину. Двойной клик — вернуть по умолчанию"
                onMouseDown={event =>
                    startColumnDrag(event, { minWidth: minWidth || MIN_COLUMN_WIDTH, onResize })
                }
                onClick={event => event.stopPropagation()}
                onDoubleClick={event => {
                    event.stopPropagation();
                    onResetWidth?.();
                }}
            />
        </th>
    );
};

export const resizableTableComponents = {
    header: { cell: ResizableTitle }
};

const columnKey = column =>
    column.key ||
    (Array.isArray(column.dataIndex) ? column.dataIndex.join('.') : column.dataIndex);

/**
 * Применяет сохранённые ширины к колонкам antd Table и добавляет ручку ресайза.
 * widths — объект { [tableId]: { [columnKey]: px } } из useUserUiSetting.
 * Возвращает { columns, scrollX } — scrollX = сумма ширин + extraWidth
 * (например, колонка выбора строк).
 */
export const useResizableColumns = (columns, tableId, widths, setWidths, extraWidth = 0) =>
    useMemo(() => {
        const saved = widths?.[tableId] || {};

        const result = columns.map(column => {
            const key = columnKey(column);
            if (!key || !column.width) return column;

            const width = saved[key] || column.width;

            return {
                ...column,
                width,
                onHeaderCell: col => ({
                    ...(column.onHeaderCell ? column.onHeaderCell(col) : {}),
                    width,
                    onResize: nextWidth =>
                        setWidths(prev => ({
                            ...(prev || {}),
                            [tableId]: { ...(prev?.[tableId] || {}), [key]: nextWidth }
                        })),
                    onResetWidth: () =>
                        setWidths(prev => {
                            const table = { ...(prev?.[tableId] || {}) };
                            delete table[key];
                            return { ...(prev || {}), [tableId]: table };
                        })
                })
            };
        });

        const scrollX =
            result.reduce((sum, column) => sum + (Number(column.width) || 100), 0) + extraWidth;

        return { columns: result, scrollX };
    }, [columns, tableId, widths, setWidths, extraWidth]);
